// Product catalog behind /products and /products/{slug}. Pure data (no JSX, no
// path aliases) so the build can read it too: vite.config.ts uses it to write
// a static HTML shell, sitemap entry and meta tags per page.
//
// Claims here must match docs/product/FEATURES.md — that file is the source of
// truth for what is built. Status words are the same three it uses.
export type FeatureStatus = "available" | "finishing" | "planned";
export type ProductStatus = "live" | "in-development" | "planned";

export interface ProductFeature {
  name: string;
  status: FeatureStatus;
}

export interface FeatureGroup {
  title: string;
  features: ProductFeature[];
}

export interface Product {
  slug: string;
  name: string;
  icon: string;
  status: ProductStatus;
  /** One line for cards and menus. */
  tagline: string;
  /** Page title and meta description (kept under ~160 characters). */
  seoTitle: string;
  seoDescription: string;
  /** Opening paragraph on the product page. */
  summary: string;
  groups: FeatureGroup[];
}

const a = (name: string): ProductFeature => ({ name, status: "available" });
const f = (name: string): ProductFeature => ({ name, status: "finishing" });
const p = (name: string): ProductFeature => ({ name, status: "planned" });

export const PRODUCTS: Product[] = [
  {
    slug: "accounting",
    name: "Accounting",
    icon: "receipt",
    status: "live",
    tagline: "Double-entry books, invoicing, banking and tax.",
    seoTitle: "Online accounting software — invoicing, banking, tax | SYLVIQ",
    seoDescription:
      "Accounting built with Niche Accounting: invoices, bills, bank reconciliation, fixed assets, multi-currency and tax returns. Live today.",
    summary:
      "SYLVIQ hosts your organization's apps, and we built Accounting in partnership with the accountants at Niche Accounting. It brings the ledger, invoicing, bills, banking, fixed assets, and tax together in one place.",
    groups: [
      {
        title: "Sales and getting paid",
        features: [
          a("Invoices, credit notes, prepayments and sales receipts"),
          a("Quotes customers accept online, converted to invoices"),
          a("Recurring invoices and deferred-revenue schedules"),
          a("Shareable links for customers to view invoices and statements, and accept or decline quotes online"),
          a("Invoice PDFs and customizable templates"),
          f("Automatic payment reminders"),
          p("Customers paying online with Stripe"),
        ],
      },
      {
        title: "Bills and spending",
        features: [
          a("Bills, supplier credits and recurring bills"),
          a("Expense claims with approval"),
          a("Payment batches, supplier statements and remittance PDFs"),
          a("Landed costs on stock"),
          p("Paying by ACH, SEPA or printed check"),
        ],
      },
      {
        title: "Banking",
        features: [
          a("Import bank statements (CSV, OFX, QIF, CAMT, MT940, BAI2)"),
          a("Reconciliation with suggested matches and bank rules"),
          p("Live bank feeds"),
        ],
      },
      {
        title: "Ledger, closing and assets",
        features: [
          a("Chart of accounts with country templates for nine countries"),
          a("Journal entries, recurring journals, accruals and an audit log"),
          a("Fiscal years, period locks and year-end close"),
          a(
            "Fixed assets with four depreciation methods, disposals and revaluation",
          ),
          a("Loans with amortization schedules"),
          a("Budgets and budget-versus-actual"),
          a("Multi-entity consolidation with eliminations"),
          a("Multi-currency (rates entered by hand)"),
          a("Job costing and time tracking with profitability"),
        ],
      },
      {
        title: "Tax and reports",
        features: [
          a("Sales tax and VAT/GST rates, jurisdictions and returns"),
          a("1099 contractor report"),
          a("Trial balance, balance sheet, profit and loss, cash flow"),
          a("Aged receivables and payables, general-ledger query"),
          a("Excel downloads of the main reports"),
          a("180-day cash-flow forecast"),
          p("Custom report builder and scheduled reports"),
        ],
      },
    ],
  },
  {
    slug: "inventory",
    name: "Inventory & operations",
    icon: "package",
    status: "in-development",
    tagline: "Stock, locations and valuation for a physical business.",
    seoTitle: "Inventory and operations software | SYLVIQ",
    seoDescription:
      "Track items, locations and stock movements with valuation. In development at SYLVIQ.",
    summary:
      "Stock levels, locations and valuation for a physical business. The engine is built; the screens are being finished.",
    groups: [
      {
        title: "Stock",
        features: [
          a("Inventory items, locations and stock movements"),
          a("Stock valuation"),
          a("Landed costs"),
          p("Assembly and bills of materials"),
          p("Stock takes and transfers"),
        ],
      },
      {
        title: "Buying",
        features: [
          f("Purchase requisitions, purchase orders and goods receipts"),
          f("Three-way match against bills"),
          p("Turn a purchase order into a bill"),
        ],
      },
    ],
  },
  {
    slug: "crm",
    name: "CRM & sales",
    icon: "contact",
    status: "planned",
    tagline: "One customer record shared with accounting.",
    seoTitle: "CRM and sales pipeline | SYLVIQ",
    seoDescription:
      "Contacts and customer records shared with your accounting, then a sales pipeline in the same organization. Planned for SYLVIQ.",
    summary:
      "Your customers already live in SYLVIQ through accounting. The CRM extends that single record with a pipeline instead of a second database to keep in sync.",
    groups: [
      {
        title: "Customers",
        features: [
          a("Contacts shared between accounting and sales"),
          a("Credit-limit warnings and blocking"),
          a("Customer portal with statements and e-signature"),
        ],
      },
      {
        title: "Selling",
        features: [
          p("Sales pipeline and deal tracking"),
          p("Quote-to-cash from the pipeline"),
        ],
      },
    ],
  },
  {
    slug: "workspace",
    name: "Organization & admin",
    icon: "building",
    status: "live",
    tagline: "Organizations, roles, entities and audit in one tenancy model.",
    seoTitle: "Multi-entity organizations, roles and audit | SYLVIQ",
    seoDescription:
      "Organizations, legal entities, invitations, roles and permissions, two-factor authentication and API tokens on one account.",
    summary:
      "The layer every SYLVIQ app shares: one sign-in, one set of people and permissions, and a record of who did what.",
    groups: [
      {
        title: "People and access",
        features: [
          a("One account for every app, with email confirmation"),
          a("Two-factor authentication and API tokens"),
          a("Roles and permissions per organization"),
          a("Invitations for teammates and advisors"),
        ],
      },
      {
        title: "Structure and safety",
        features: [
          a("Organizations and legal entities"),
          a("Backup and restore of your books"),
          a("Audit log of changes"),
          a("Subscription billing and customer portal for your SYLVIQ plan"),
        ],
      },
    ],
  },
  {
    slug: "passwords",
    name: "SYLVIQ Passwords",
    icon: "credit-card",
    status: "in-development",
    tagline: "An encrypted password vault, with or without an account.",
    seoTitle: "SYLVIQ Passwords — private encrypted password manager",
    seoDescription:
      "Keep passwords in a local encrypted vault or sync encrypted data across devices with a SYLVIQ account. SYLVIQ cannot read your vault.",
    summary:
      "A password vault designed to work on its own or with your SYLVIQ account. Your vault passphrase stays separate from account sign-in, and only encrypted vault data is synced.",
    groups: [
      {
        title: "Your vault",
        features: [
          f("Local encrypted vault that does not require an account"),
          f("Encrypted account sync between devices"),
          f("Locally generated passwords and encrypted backups"),
          f("One-time code-assisted encrypted device transfer"),
        ],
      },
      {
        title: "Coming next",
        features: [
          p("Browser autofill and extension clients"),
          p("Native iOS and Android credential clients"),
          p("Passkey-provider support"),
        ],
      },
    ],
  },
  {
    slug: "authenticator",
    name: "SYLVIQ Authenticator",
    icon: "scan-barcode",
    status: "in-development",
    tagline: "Private two-factor codes, on your device or across devices.",
    seoTitle: "SYLVIQ Authenticator — private two-factor codes",
    seoDescription:
      "Generate TOTP codes from an encrypted local vault, with optional encrypted account sync and manual device transfer. No account required.",
    summary:
      "Keep your two-factor authenticator seeds in an encrypted local vault, or carry the encrypted vault between devices with a SYLVIQ account. Codes are generated on your device.",
    groups: [
      {
        title: "Authenticator vault",
        features: [
          f("Locally generated RFC 6238 TOTP codes"),
          f("Add accounts from a Base32 secret or otpauth setup link"),
          f("Encrypted local storage and optional account sync"),
          f("Encrypted export and one-time code-assisted device transfer"),
        ],
      },
      {
        title: "Coming next",
        features: [
          p("Camera scanning for setup QR codes"),
          p("Native iOS and Android apps"),
        ],
      },
    ],
  },
];

export function findProduct(slug: string): Product | undefined {
  return PRODUCTS.find((product) => product.slug === slug);
}

export const STATUS_LABEL: Record<ProductStatus, string> = {
  live: "Live today",
  "in-development": "In development",
  planned: "Planned",
};

export const FEATURE_STATUS_LABEL: Record<FeatureStatus, string> = {
  available: "Available",
  finishing: "Finishing",
  planned: "Planned",
};

export const SITE_ORIGIN = "https://www.sylviq.io";
